import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import PayPalModal from "@/components/PayPalModal";
import CashAppModal from "@/components/CashAppModal";
import {
  ArrowRight,
  Award,
  BookOpen,
  Check,
  FileCheck2,
  GraduationCap,
  ShieldCheck,
  Sparkles,
  Wallet,
  Scale,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PLAN_AFSP = { id: "afsp_prep", name: "AFSP Prep Bundle", amount: 199 };

/**
 * Landing page for the standalone $199 AFSP Prep Bundle:
 * IRS Circular 230 study guide (PDF) + 18-question AFTR practice exam.
 * Aimed at seasoned tax preparers — does NOT bundle any 1040 content.
 */
export default function AfspBundle() {
  const { branding } = useBranding();
  const { user } = useAuth();
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [paypalHandle, setPaypalHandle] = useState("Dynamictax80");
  const [cashTag, setCashTag] = useState("$DimekaH");

  useEffect(() => {
    axios.get(`${API}/api/payments/plans`).then((r) => {
      setPaypalHandle(r.data?.paypal_handle || "Dynamictax80");
      setCashTag(r.data?.cash_app_tag || "$DimekaH");
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="afsp-bundle-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" />
            ) : (
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">
                {branding.brand_name?.[0]}
              </div>
            )}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">Home</Link>
            <Link to="/1040-course" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">1040 Course</Link>
            <Link to="/1120-refresher" className="hidden sm:inline underline text-muted-foreground hover:text-foreground">1120 Refresher</Link>
            <Link to="/pricing" className="hidden sm:inline underline text-primary hover:text-primary/80 font-medium">Pricing</Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(214,138,61,0.22), transparent 60%), linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5 text-accent text-xs uppercase tracking-widest mb-6">
            <Scale size={14} /> AFTR Exam Prep · Seasoned preparers only
          </div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] text-white mb-6">
            Pass the <span className="italic text-accent">AFTR</span>.<br />
            Earn your AFSP <span className="italic text-accent">Record of Completion</span>.
          </h1>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-8">
            A focused, no-fluff prep pack built for tax pros who already prepare returns.
            The full IRS Circular 230 study guide plus an 18-question AFTR-style exam that
            tests every domain — with rationale after every answer.
          </p>

          <div className="mt-8 inline-flex items-baseline gap-3">
            <span className="font-serif text-6xl md:text-7xl text-white">$199</span>
            <span className="text-sm text-white/60">/ one-time</span>
          </div>
          <div className="mt-2 text-xs text-accent font-medium tracking-wide">
            LIFETIME ACCESS · UNLIMITED QUIZ RETAKES
          </div>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button
              size="lg"
              className="bg-[#0070ba] hover:bg-[#005ea6] text-white"
              onClick={() => setPaypalOpen(true)}
              data-testid="afsp-cta-paypal"
            >
              <Wallet size={16} className="mr-2" /> Pay $199 with PayPal
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/40 text-white hover:bg-white/10"
              onClick={() => setCashOpen(true)}
              data-testid="afsp-cta-cashapp"
            >
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            {user?.has_afsp_access && (
              <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10" data-testid="afsp-cta-start">
                <Link to="/afsp-prep">
                  Start prepping now <ArrowRight size={16} className="ml-2" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">This is NOT a beginner course</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              Built for tax pros who already <span className="italic text-primary">know the work.</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              We skip the "what is a W-2" chapters. You already do the returns. What you need is a tight
              refresh on ethics, due diligence, and current-year updates — plus practice questions that
              mirror the real AFTR blueprint.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AUDIENCE.map((a, i) => (
              <div key={i} className="p-6 rounded-lg border border-border bg-card" data-testid={`afsp-audience-${i}`}>
                <div className="h-10 w-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-4">
                  {a.icon}
                </div>
                <h3 className="font-serif text-xl mb-2">{a.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">What's in the $199</div>
            <h2 className="font-serif text-4xl md:text-5xl">Two assets. Zero fluff.</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-8 rounded-lg border-2 border-primary/40 bg-card" data-testid="afsp-asset-pdf">
              <div className="h-12 w-12 rounded-md bg-primary/10 text-primary grid place-items-center mb-5">
                <FileCheck2 size={22} />
              </div>
              <h3 className="font-serif text-2xl mb-2">IRS Circular 230 Study Guide</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                The full IRS Treasury Department Circular No. 230 — the actual document that
                governs practice before the IRS. Downloadable PDF, print-ready, keep it forever.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-primary" /> §10.22 Due diligence</li>
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-primary" /> §10.29 Conflicting interests</li>
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-primary" /> §10.34 Standards for advising positions</li>
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-primary" /> §10.50 Sanctions & disciplinary actions</li>
              </ul>
            </div>

            <div className="p-8 rounded-lg border-2 border-accent/40 bg-card" data-testid="afsp-asset-quiz">
              <div className="h-12 w-12 rounded-md bg-accent/10 text-accent grid place-items-center mb-5">
                <GraduationCap size={22} />
              </div>
              <h3 className="font-serif text-2xl mb-2">18-Question AFTR-Style Practice Exam</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                Modeled on the AFTR exam blueprint. Every question is followed by the correct answer
                and a plain-English rationale so you learn while you review. Retake as many times as you want.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-accent" /> Domain 1 — New Tax Law / Updates</li>
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-accent" /> Domain 2 — General Review</li>
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-accent" /> Domain 3 — Ethics, Practices & Procedures</li>
                <li className="flex items-start gap-2"><Check size={13} className="mt-1 shrink-0 text-accent" /> Passing threshold: 70% — matches the real AFTR</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="border-t border-border bg-secondary/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Why the AFSP matters</div>
            <h2 className="font-serif text-4xl md:text-5xl">
              Get listed in the <span className="italic text-primary">IRS Directory.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {WHY_AFSP.map((item, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`afsp-why-${i}`}>
                <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                  <Check size={13} />
                </div>
                <span className="text-sm leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center mb-10">
            <div className="label-caps mb-3 text-primary">Frequently asked</div>
            <h2 className="font-serif text-4xl md:text-5xl">The short version.</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="afsp-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`afsp-faq-item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 text-center">
          <h2 className="font-serif text-4xl md:text-6xl mb-4">
            $199. <span className="italic text-primary">One time.</span> Lifetime access.
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            Get AFSP-ready this week. Circular 230 PDF, 18-question practice exam with rationales, unlimited retakes.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button size="lg" className="bg-[#0070ba] hover:bg-[#005ea6] text-white" onClick={() => setPaypalOpen(true)} data-testid="afsp-final-cta-paypal">
              <Wallet size={16} className="mr-2" /> Pay $199 with PayPal
            </Button>
            <Button size="lg" variant="outline" onClick={() => setCashOpen(true)} data-testid="afsp-final-cta-cashapp">
              <Wallet size={16} className="mr-2" /> Pay with Cash App
            </Button>
            {user?.has_afsp_access && (
              <Button asChild size="lg" variant="ghost" data-testid="afsp-final-cta-start">
                <Link to="/afsp-prep">
                  Start prepping <ArrowRight size={16} className="ml-2" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>Powered by <span className="text-foreground">{branding.brand_name}</span></div>
          <div className="flex gap-4">
            <Link to="/" className="underline hover:text-foreground">Home</Link>
            <Link to="/1040-course" className="underline hover:text-foreground">1040 Course</Link>
            <Link to="/1120-refresher" className="underline hover:text-foreground">1120 Refresher</Link>
            <Link to="/pricing" className="underline hover:text-foreground">Pricing</Link>
          </div>
        </div>
      </footer>

      <PayPalModal
        open={paypalOpen}
        onClose={() => setPaypalOpen(false)}
        plan={PLAN_AFSP}
        paypalHandle={paypalHandle}
        user={user}
      />
      <CashAppModal
        open={cashOpen}
        onClose={() => setCashOpen(false)}
        plan={PLAN_AFSP}
        cashTag={cashTag}
        user={user}
      />
    </div>
  );
}

const AUDIENCE = [
  { icon: <BookOpen size={22} />, title: "Preparing returns already", body: "You've done 1040s (or corporate returns) for at least a season and want to add the AFSP designation to your practice." },
  { icon: <Award size={22} />, title: "Chasing the IRS Directory listing", body: "You want to appear in the IRS Directory of Federal Tax Return Preparers with Credentials — that credibility bump matters at closing." },
  { icon: <ShieldCheck size={22} />, title: "Renewing your AFSP each year", body: "You already earned the AFSP before and need a fast, current refresh on ethics + Circular 230 before the next season kicks off." },
];

const WHY_AFSP = [
  "Appear in the public IRS Directory of Federal Tax Return Preparers with Credentials — new clients search it before hiring.",
  "Gain limited representation rights before the IRS for returns you prepared (much stronger than a bare PTIN).",
  "Signal to clients you meet the IRS's current standards on ethics, due diligence, and tax updates.",
  "Show up in tax software directories as an AFSP-credentialed preparer — bigger firms filter by this.",
  "Justify higher fees. AFSP preparers routinely charge $50–$150 more per return than unenrolled PTIN-only preparers.",
  "Stay current on every tax year. Circular 230 refreshers keep you sharp on ethics, sanctions, and preparer conduct.",
];

const FAQS = [
  {
    q: "Does this include the 1040 course?",
    a: "No — by design. This bundle is for seasoned preparers who already know how to prepare returns. You get the Circular 230 study guide PDF and the 18-question AFTR practice exam. If you also want the full 1040 curriculum, grab the 1040 course separately.",
  },
  {
    q: "Is this the actual AFTR exam?",
    a: "No. This is a practice exam modeled on the AFTR blueprint. The actual AFTR is administered through IRS-approved CE providers and is 100 questions, 3 hours, 70% to pass. Our exam is designed to get you exam-ready by testing the same domains with the same style of question.",
  },
  {
    q: "How does the AFSP program actually work?",
    a: "The AFSP is a voluntary IRS program. Non-credentialed preparers earn a Record of Completion by (1) completing 18 hours of continuing education from an IRS-approved provider (including the AFTR course and exam), (2) having a valid PTIN, and (3) consenting to Circular 230 duties. This bundle helps you get ready for the AFTR portion.",
  },
  {
    q: "Does the practice exam give me CE credit?",
    a: "No. This is study material only, not an IRS-approved CE provider offering. To earn CE hours toward AFSP, you complete the AFTR course & exam with an IRS-approved CE provider. Use this bundle to study efficiently before that.",
  },
  {
    q: "Can I retake the practice exam?",
    a: "Yes — unlimited retakes. Your best score is tracked in your dashboard so you can see your progress over time.",
  },
  {
    q: "What's the refund policy?",
    a: "Because the PDF is downloadable and the quiz unlocks immediately, all sales are final once you access either. If you have not downloaded the PDF or started the quiz, you can request a refund within 7 days.",
  },
];
